// theme.ts — FUENTE del tema (esbuild → public/theme.js). Se sirve SYNC en el <head> ANTES del CSS de cada
// página: resuelve el tema sin flash y cablea los botones [data-theme-toggle]. La elección manual vive en
// localStorage('movai-theme'); sin elección, sigue al sistema (y a sus cambios en vivo). Emite el evento
// 'movai:theme' en window para que las páginas reaccionen (p. ej. Insights re-monta sus gráficos).
// Sin imports a propósito: es un bundle IIFE autónomo que corre antes que nada.

type Theme = 'light' | 'dark';

declare global {
  interface Window {
    movaiTheme?: { get: () => Theme; set: (t: Theme) => void; toggle: () => void };
  }
}

(function () {
  const KEY = 'movai-theme';
  const mq = window.matchMedia('(prefers-color-scheme: light)');

  // Iconos sol/luna (mismo estilo de trazo que el set ICON del portal). El icono muestra el DESTINO:
  // en oscuro se ve el sol ("cambiar a claro") y viceversa.
  const SUN =
    '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="4"/><line x1="12" y1="2" x2="12" y2="4"/><line x1="12" y1="20" x2="12" y2="22"/><line x1="4.9" y1="4.9" x2="6.3" y2="6.3"/><line x1="17.7" y1="17.7" x2="19.1" y2="19.1"/><line x1="2" y1="12" x2="4" y2="12"/><line x1="20" y1="12" x2="22" y2="12"/><line x1="4.9" y1="19.1" x2="6.3" y2="17.7"/><line x1="17.7" y1="6.3" x2="19.1" y2="4.9"/></svg>';
  const MOON =
    '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z"/></svg>';

  function stored(): Theme | null {
    try {
      const v = localStorage.getItem(KEY);
      return v === 'light' || v === 'dark' ? v : null;
    } catch {
      return null;
    }
  }
  const systemTheme = (): Theme => (mq.matches ? 'light' : 'dark');
  const current = (): Theme => (document.documentElement.dataset.theme === 'light' ? 'light' : 'dark');

  function paintToggles(theme: Theme): void {
    // Bilingüe sin importar i18n (este bundle corre antes que todo): el lang del documento manda
    // (en /en el generador fija lang="en"; applyI18n también lo cambia al traducir).
    const en = document.documentElement.lang === 'en';
    const label =
      theme === 'dark'
        ? en
          ? 'Switch to light theme'
          : 'Cambiar a tema claro'
        : en
          ? 'Switch to dark theme'
          : 'Cambiar a tema oscuro';
    document.querySelectorAll<HTMLElement>('[data-theme-toggle]').forEach((btn) => {
      btn.innerHTML = theme === 'dark' ? SUN : MOON;
      btn.setAttribute('aria-label', label);
    });
  }

  function apply(theme: Theme): void {
    document.documentElement.dataset.theme = theme;
    // El chrome del navegador móvil sigue al tema (los valores espejan --bg de tokens.css).
    document
      .querySelector('meta[name="theme-color"]')
      ?.setAttribute('content', theme === 'dark' ? '#0a0716' : '#f6f6fb');
    paintToggles(theme);
    window.dispatchEvent(new CustomEvent('movai:theme', { detail: { theme } }));
  }

  window.movaiTheme = {
    get: current,
    set(theme: Theme) {
      try {
        localStorage.setItem(KEY, theme);
      } catch {
        /* almacenamiento bloqueado: el tema aplica solo para esta vista */
      }
      apply(theme);
    },
    toggle() {
      window.movaiTheme!.set(current() === 'dark' ? 'light' : 'dark');
    },
  };

  // Sin elección manual, el tema sigue al sistema también en vivo.
  mq.addEventListener('change', () => {
    if (!stored()) apply(systemTheme());
  });

  // Resolución inmediata (pre-CSS): cero flash.
  document.documentElement.dataset.theme = stored() || systemTheme();

  // Cableado por delegación: cualquier [data-theme-toggle] presente o futuro funciona.
  document.addEventListener('click', (e) => {
    const target = e.target as HTMLElement | null;
    if (target?.closest?.('[data-theme-toggle]')) window.movaiTheme!.toggle();
  });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => paintToggles(current()));
  } else {
    paintToggles(current());
  }
})();

export {};
